<template>
  <div>
    <HeroHeader
      eyebrow="GESTIÓN"
      title="Expositores"
      :counter-num="String(filteredList.length)"
      :counter-unit="filteredCounterUnit"
    >
      <template #toolbar>
        <button class="btn" @click="showNewSidebar = true">+ Agregar</button>
      </template>
    </HeroHeader>

    <!-- Filtro por propiedad — las mismas pastillas que usa la app. Cambia de qué
         local se está viendo; la barra de abajo sigue siendo el estado. En
         «Todas» la lista no se aplana: se parte en secciones por propiedad.
         Eran pestañas subrayadas, pero el juego lo pone el dato (trece hoy) y al
         envolverse en dos filas el subrayado del activo quedaba flotando a media
         altura, despegado de la línea de la barra. Una pastilla se lee igual de
         bien envuelta que en fila. -->
    <div class="filters">
      <div class="chips-row">
        <span class="chips-row__label" id="f-propiedad">Propiedad</span>
        <ChipsBar :chips="estateChips" v-model="filterEstate" wrap aria-labelledby="f-propiedad" />
      </div>
      <div class="chips-row">
        <span class="chips-row__label" id="f-estado">Estado</span>
        <ChipsBar :chips="statusChips" v-model="filterGood" aria-labelledby="f-estado" />
      </div>
    </div>

    <div class="filter-bar filter-bar--no-top">
      <SearchInput v-model="search" placeholder="Buscar por alias o documento…"/>
      <!-- Sin @change: `filterState` ya tiene watcher. Con los dos, cada cambio
           lanzaba DOS cargas concurrentes que se duplicaban entre sí -->
      <select v-model="filterState" aria-label="Estado de la ficha">
        <option value="true">Activos</option>
        <option value="false">Inactivos</option>
      </select>
      <button v-if="hasFilters" type="button" class="filter-bar__clear" @click="clearFilters">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
        Limpiar filtros
      </button>
      <SortSelect :options="sortOptions" v-model="sortBy" />
    </div>

    <div v-if="sortedList.length > 0">
      <template v-for="g in groups" :key="g.key">
        <!-- Cabecera de sección: sólo con «Todos» activo. Con una propiedad
             seleccionada el nombre ya lo lleva la pestaña. Es el botón que
             pliega el bloque, así que el conteo tiene que seguir visible
             plegado — es lo único que queda de la propiedad. -->
        <button
          v-if="g.title"
          type="button"
          class="list-section"
          :class="{ 'list-section--collapsed': !g.open }"
          :aria-expanded="String(g.open)"
          @click="toggleGroup(g.key)"
        >
          <span class="list-section__chevron" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </span>
          <span class="list-section__title">{{ g.title }}</span>
          <span class="list-section__meta">
            {{ g.rows.length }} {{ g.rows.length === 1 ? 'expositor' : 'expositores' }}<template v-if="g.overdue"> · <span class="list-section__alert">{{ g.overdue }} {{ g.overdue === 1 ? 'atrasado' : 'atrasados' }}</span></template>
          </span>
        </button>

        <div
          v-for="c in (g.open ? g.rows : [])"
          :key="c.id"
          class="tenant-row tenant-row--clickable"
          :class="tenantRowClass(c)"
          @click="$router.push({name:'tenant-detail',params:{id:c.id},query:{tab:'bills'}})"
        >
          <div class="tenant-row__body">
            <div class="tenant-row__name">{{ c.alias }}</div>
            <!-- La propiedad se queda en la fila aunque la cabecera de sección y la
                 pestaña también la digan: las dos se van con el scroll, y a la fila 40
                 de Royal Home 2 —67 fichas, más de 4.000px— no quedaría nada en
                 pantalla que conteste «¿de qué galería es esta?». La app hace lo mismo
                 pese a tener sus chips de propiedad. -->
            <div class="tenant-row__meta">
              {{ [c.local ? c.local.description : null, c.day ? 'Día ' + c.day : null].filter(Boolean).join(' · ') }}
            </div>
          </div>
          <div class="tenant-row__right">
            <span class="tenant-row__status" :class="statusClass(c)">{{ statusLabel(c) }}</span>
            <span v-if="c.rent" class="tenant-row__detail">{{ formatRent(c.rent, c.currency) }}/mes</span>
            <span v-if="!c.good && c.overdueSince" class="tenant-row__detail">desde {{ overduePeriod(c.overdueSince) }}</span>
            <span v-if="expiryLabel(c.end)" class="tenant-row__detail" style="color:#D97706">{{ expiryLabel(c.end) }}</span>
          </div>
          <div class="tenant-row__actions">
            <span class="dt-icon" title="Editar" @click.stop="showUpdateSidebar = true; sidebarData = c">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </span>
            <span v-if="!c.good" class="dt-icon" title="Notificar" @click.stop="showSendNot = true; sidebarData = c">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="none" stroke="currentColor"
                   stroke-width="32" stroke-linecap="round" stroke-linejoin="round">
                <path d="M427.68 351.43C402 320 383.87 304 383.87 217.35 383.87 138 343.35 109.73 310 96c-4.43-1.82-8.6-6-9.95-10.55C294.2 65.54 277.8 48 256 48s-38.21 17.55-44 37.47c-1.35 4.6-5.52 8.71-9.95 10.53-33.39 13.75-73.87 41.92-73.87 121.35C128.13 304 110 320 84.32 351.43 73.68 364.45 83 384 101.61 384h308.88c18.51 0 27.77-19.61 17.19-32.57zM320 384v16a64 64 0 01-128 0v-16"/>
              </svg>
            </span>
            <span v-if="!c.hasAccess" class="dt-icon" title="Crear acceso" @click.stop="showAccessSidebar = true; sidebarData = c">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="none" stroke="currentColor"
                   stroke-width="32" stroke-linecap="round" stroke-linejoin="round">
                <path d="M218.1 167.17c0 13 0 25.6 4.1 37.4-43.1 50.6-156.9 184.3-167.5 194.5a20.17 20.17 0 00-6.7 15c0 8.5 5.2 16.7 9.6 21.3 6.6 6.9 34.8 33 40 28 15.4-15 18.5-19 24.8-25.2 9.5-9.3-1-28.3 2.3-36s7.3-9.2 12.7-10.4 18.6 4.6 24.6 1.7c5.4-2.7 12.4-12.6 17.6-18.8 9.5-11.3-3.7-26.6 1.3-36s17.8-11.3 24.4-15.9c5-3.5 1.6 4.6 8.6 5.8 12.9 2.3 27 0 44.1 0 70.3 0 127.4-58.9 127.4-131.5S401.7 36 331.4 36s-113.3 58.6-113.3 131.17zM385 142.5a35.5 35.5 0 11-35.49-35.5A35.5 35.5 0 01385 142.5z"/>
              </svg>
            </span>
            <span v-if="c.hasAccess" class="dt-icon" title="Gestionar acceso" @click.stop="showManageSidebar = true; sidebarData = c">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="none" stroke="currentColor"
                   stroke-width="32" stroke-linecap="round" stroke-linejoin="round">
                <path d="M262.29 192.31a64 64 0 1057.4 57.4 64.13 64.13 0 00-57.4-57.4zM416.39 256a154.34 154.34 0 01-1.53 20.79l45.21 35.46a10.81 10.81 0 012.45 13.75l-42.77 74a10.81 10.81 0 01-13.14 4.59l-44.9-18.08a16.11 16.11 0 00-15.17 1.75A164.48 164.48 0 01325 400.8a15.94 15.94 0 00-8.82 12.14l-6.73 47.89a11.08 11.08 0 01-10.68 9.17h-85.54a11.11 11.11 0 01-10.69-8.87l-6.72-47.82a16.07 16.07 0 00-9-12.22 155.3 155.3 0 01-21.46-12.57 16 16 0 00-15.11-1.71l-44.89 18.07a10.81 10.81 0 01-13.14-4.58l-42.77-74a10.8 10.8 0 012.45-13.75l38.21-30a16.05 16.05 0 006-14.08c-.36-4.17-.58-8.36-.58-12.6s.21-8.42.57-12.59a16 16 0 00-6.07-13.94l-38.19-30A10.81 10.81 0 0149.48 186l42.77-74a10.81 10.81 0 0113.14-4.59l44.9 18.08a16.11 16.11 0 0015.17-1.75A164.48 164.48 0 01187 111.2a15.94 15.94 0 008.82-12.14l6.73-47.89A11.08 11.08 0 01213.23 42h85.54a11.11 11.11 0 0110.69 8.87l6.72 47.82a16.07 16.07 0 009 12.22 155.3 155.3 0 0121.46 12.57 16 16 0 0015.11 1.71l44.89-18.07a10.81 10.81 0 0113.14 4.58l42.77 74a10.8 10.8 0 01-2.45 13.75l-38.21 30a16.05 16.05 0 00-6 14.08c.33 4.14.55 8.33.55 12.49z"/>
              </svg>
            </span>
          </div>
        </div>
      </template>
    </div>

    <EmptyState v-else title="Sin inquilinos" message="No hay inquilinos para mostrar con los filtros actuales."/>

    <NewTenant :estates="estates" @add="addRow" @close="showNewSidebar = false"
               :class="{'show': showNewSidebar}" title="Nuevo inquilino" :show-navbar="showNewSidebar"/>
    <UpdateTenant :estates="estates" :data="sidebarData" @update="updateRow" @close="showUpdateSidebar = false"
                  :class="{'show': showUpdateSidebar}" title="Editar inquilino" :show-navbar="showUpdateSidebar"/>
    <SendNotification :data="sidebarData" @send="sendNotification"
                      :class="{'show': showSendNot}" @close="showSendNot = false" :show-navbar="showSendNot"/>
    <AccessTenant :data="sidebarData" @granted="grantedRow" @close="showAccessSidebar = false"
                  :class="{'show': showAccessSidebar}" title="Crear acceso de expositor" :show-navbar="showAccessSidebar"/>
    <ManageAccess :data="sidebarData" @revoked="revokedRow" @reset="() => {}"
                  @close="showManageSidebar = false" :class="{'show': showManageSidebar}"
                  title="Gestionar acceso" :show-navbar="showManageSidebar"/>
  </div>
</template>

<script>
import NewTenant from './NewTenant'
import UpdateTenant from './UpdateTenant'
import AccessTenant from './AccessTenant'
import ManageAccess from './ManageAccess'
import SendNotification from './SendNotification'
import HeroHeader from '@/components/HeroHeader'
import ChipsBar from '@/components/ChipsBar'
import EmptyState from '@/components/EmptyState'
import SortSelect from '@/components/SortSelect'
import SearchInput from '@/components/SearchInput'
import { sortItems } from '@/utils/sort'
import { normalizeText, matches } from '@/utils/text'
import { db, storage, Timestamp } from '@/firebase/firebaseConfig'
import axios from 'axios'

// Bucket para las fichas sin propiedad asignada. No colisiona con un id de
// Firestore (20 caracteres) y mantiene la agrupación total: ninguna fila puede
// quedarse sin sección.
const NO_ESTATE = 'none'

export default {
  name: 'Tenants',
  components: { NewTenant, UpdateTenant, AccessTenant, ManageAccess, SendNotification, HeroHeader, ChipsBar, EmptyState, SortSelect, SearchInput },
  data () {
    return {
      list: [],
      estates: [],
      search: '',
      filterEstate: 'All',
      filterState: 'true',
      loadGen: 0,
      // Secciones ABIERTAS, por id de propiedad. Se guarda lo abierto y no lo
      // plegado para que el defecto sea plegado: con trece propiedades y 153
      // fichas, abrirlas todas de entrada son más de 9.000px de lista antes de
      // que se pueda decidir nada. Plegado, la pantalla entra en un vistazo y
      // cada cabecera lleva su conteo. Una propiedad nueva nace plegada como el
      // resto, sin caso especial.
      expanded: {},
      filterGood: 'all',
      sortBy: 'status',
      sortOptions: [
        { value: 'status', label: 'Atrasados primero' },
        { value: 'name', label: 'Nombre (A–Z)', field: 'alias', type: 'string', dir: 'asc' },
        { value: 'name_desc', label: 'Nombre (Z–A)', field: 'alias', type: 'string', dir: 'desc' },
        { value: 'rent_desc', label: 'Renta (mayor a menor)', field: 'rent', type: 'number', dir: 'desc' },
        { value: 'recent', label: 'Más recientes', field: 'createdAt', type: 'date', dir: 'desc' }
      ],
      showNewSidebar: false,
      showUpdateSidebar: false,
      showAccessSidebar: false,
      showManageSidebar: false,
      showSendNot: false,
      sidebarData: {}
    }
  },
  computed: {
    // Base de los dos contadores facetados: las pestañas cuentan con el estado
    // ya aplicado y los chips con la propiedad ya aplicada, así cada número es
    // exactamente lo que se ve al hacer click en él.
    searched () {
      const q = normalizeText(this.search)
      if (!q) return this.list
      return this.list.filter(c => matches(c.alias, q) || matches(c.document, q))
    },
    // Cuántas fichas tiene cada propiedad en el estado actual (activos o
    // inactivos), sin mirar búsqueda ni chips: define QUÉ pestañas existen y en
    // qué orden, para que no aparezcan y desaparezcan mientras se teclea.
    estateCounts () {
      const counts = new Map()
      this.list.forEach(c => {
        const id = this.estateIdOf(c)
        counts.set(id, (counts.get(id) || 0) + 1)
      })
      return counts
    },
    // Propiedades con fichas —más la seleccionada, que puede quedar en cero al
    // pasar de activos a inactivos—, de mayor a menor y luego alfabético. Este
    // es el orden de las pestañas Y el de las secciones: uno solo que aprender.
    orderedEstates () {
      return this.estates
        .filter(e => this.estateCounts.has(e.id) || e.id === this.filterEstate)
        .sort((a, b) => (this.estateCounts.get(b.id) || 0) - (this.estateCounts.get(a.id) || 0) ||
          (a.description || '').localeCompare(b.description || '', 'es', { sensitivity: 'base' }))
    },
    estateChips () {
      const base = this.searched.filter(c => this.matchesStatus(c))
      const counts = new Map()
      base.forEach(c => {
        const id = this.estateIdOf(c)
        counts.set(id, (counts.get(id) || 0) + 1)
      })
      const chips = this.orderedEstates.map(e => ({ label: e.description, value: e.id, count: counts.get(e.id) || 0 }))
      if (this.estateCounts.has(NO_ESTATE) || this.filterEstate === NO_ESTATE) {
        chips.push({ label: 'Sin propiedad', value: NO_ESTATE, count: counts.get(NO_ESTATE) || 0 })
      }
      // «Todas» —no «Todos»— para que no se confunda con el «Todos» de la barra
      // de estado que va justo debajo: mismas pastillas, dos preguntas distintas
      return [{ label: 'Todas', value: 'All', count: base.length }, ...chips]
    },
    statusChips () {
      const base = this.searched.filter(c => this.matchesEstate(c))
      const overdue = base.filter(c => !c.good).length
      const expiring = base.filter(c => this.isExpiring(c)).length
      return [
        { label: 'Todos', value: 'all' },
        { label: 'Atrasados', value: 'overdue', count: overdue || null },
        { label: 'Por vencer', value: 'expiring', count: expiring || null },
        { label: 'Al día', value: 'ok' }
      ]
    },
    filteredCounterUnit () {
      const overdue = this.filteredList.filter(c => !c.good).length
      if (overdue > 0) return overdue + ' con atraso'
      // El listado también muestra bajas: decir «activos» ahí era mentira
      return this.filterState === 'true' ? 'activos' : 'inactivos'
    },
    filteredList () {
      return this.searched.filter(c => this.matchesEstate(c) && this.matchesStatus(c))
    },
    sortedList () {
      // "status" mantiene el orden por defecto: atrasados primero, luego alfabético
      if (this.sortBy === 'status') {
        return [...this.filteredList].sort((a, b) => {
          if (!a.good && b.good) return -1
          if (a.good && !b.good) return 1
          return (a.alias || '').localeCompare(b.alias || '', 'es', { sensitivity: 'base' })
        })
      }
      return sortItems(this.filteredList, this.sortOptions.find(o => o.value === this.sortBy))
    },
    // Con «Todos» la lista va partida por propiedad, con una cabecera por
    // sección; con una pestaña de propiedad seleccionada es un solo bloque sin
    // cabecera, porque el nombre ya está arriba en la pestaña activa.
    groups () {
      if (this.filterEstate !== 'All') {
        // `open` es obligatorio: la plantilla renderiza `g.open ? g.rows : []`,
        // así que un bloque sin la clave no pinta ninguna fila. Sin cabecera
        // tampoco habría de dónde volver a abrirlo.
        return [{ key: 'flat', title: null, rows: this.sortedList, overdue: 0, open: true }]
      }
      const map = new Map()
      this.sortedList.forEach(c => {
        const id = this.estateIdOf(c)
        if (!map.has(id)) map.set(id, { key: id, title: this.estateNameOf(id, c), rows: [] })
        map.get(id).rows.push(c)
      })
      // Las secciones salen de las FILAS, no del catálogo: una propiedad borrada
      // del catálogo se ordena al final pero se pinta igual, para que ninguna
      // ficha desaparezca de la lista por no tener dónde ir.
      const rank = new Map(this.orderedEstates.map((e, i) => [e.id, i]))
      const at = key => (rank.has(key) ? rank.get(key) : Number.MAX_SAFE_INTEGER)
      return [...map.values()]
        .map(g => ({
          ...g,
          overdue: g.rows.filter(c => !c.good).length,
          open: !!this.expanded[g.key]
        }))
        .sort((a, b) => at(a.key) - at(b.key) ||
          b.rows.length - a.rows.length ||
          a.title.localeCompare(b.title, 'es', { sensitivity: 'base' }))
    },
    // Filtros que el usuario puso encima del estado por defecto. `filterState`
    // no cuenta: no es un filtro, es qué universo se está mirando.
    hasFilters () {
      return !!this.search || this.filterGood !== 'all' || this.filterEstate !== 'All'
    }
  },
  watch: {
    filterState () { this.loadInfo() },
    // Empezar a buscar despliega las secciones con resultados: una plegada
    // escondería fichas que el usuario acaba de pedir por su nombre. Al vaciar
    // el buscador se vuelve al defecto —todo plegado—, que es de donde salió.
    // Se hace acá y no forzando `open` en el computed porque forzarlo dejaba la
    // cabecera sin efecto visible mientras se buscaba —el chevron no rotaba,
    // `aria-expanded` no cambiaba— pero guardando igual el pliegue, que después
    // saltaba solo al vaciar el input. Un botón que no hace nada y actúa más
    // tarde es peor que no tenerlo; así el click siempre hace lo que dice.
    search (q, prev) {
      if (q && !prev) {
        this.expanded = this.sortedList.reduce((acc, c) => {
          acc[this.estateIdOf(c)] = true
          return acc
        }, {})
      } else if (!q && prev) {
        this.expanded = {}
      }
    }
  },
  async created () {
    await this.loadInfo()
    const { estateId } = this.$route.query
    if (estateId) this.filterEstate = estateId
  },
  methods: {
    toggleGroup (key) {
      // Reasignar el objeto entero: Vue 3 sí detecta la clave nueva, pero así
      // queda explícito que el mapa es de solo-lectura para el resto de la vista
      this.expanded = { ...this.expanded, [key]: !this.expanded[key] }
    },
    clearFilters () {
      this.search = ''
      this.filterGood = 'all'
      this.filterEstate = 'All'
    },
    estateIdOf (c) {
      return c.local && c.local.id ? c.local.id : NO_ESTATE
    },
    // El nombre sale del catálogo, no del snapshot guardado en la ficha: si la
    // propiedad se renombró, el snapshot queda viejo y la sección mentiría.
    estateNameOf (id, c) {
      const estate = this.estates.find(e => e.id === id)
      if (estate) return estate.description
      return (c.local && c.local.description) || 'Sin propiedad'
    },
    matchesEstate (c) {
      return this.filterEstate === 'All' || this.estateIdOf(c) === this.filterEstate
    },
    matchesStatus (c) {
      if (this.filterGood === 'overdue') return !c.good
      if (this.filterGood === 'ok') return c.good && !this.isExpiring(c)
      if (this.filterGood === 'expiring') return this.isExpiring(c)
      return true
    },
    formatRent (rent, currency) {
      const code = currency && currency.code ? currency.code : 'PEN'
      return Intl.NumberFormat('en-US', { style: 'currency', currency: code, maximumFractionDigits: 0 }).format(rent)
    },
    daysUntilExpiry (end) {
      if (!end) return null
      return Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24))
    },
    isExpiring (c) {
      const days = this.daysUntilExpiry(c.end)
      return c.good && days !== null && days <= 30
    },
    expiryLabel (end) {
      const days = this.daysUntilExpiry(end)
      if (days === null || days > 30) return ''
      return days < 1 ? 'Contrato vencido' : 'Vence en ' + days + 'd'
    },
    tenantRowClass (c) {
      if (!c.good) return 'tenant-row--overdue'
      if (this.isExpiring(c)) return 'tenant-row--expiring'
      return ''
    },
    statusClass (c) {
      if (!c.good) return 'tenant-row__status--overdue'
      if (this.isExpiring(c)) return 'tenant-row__status--expiring'
      return 'tenant-row__status--ok'
    },
    statusLabel (c) {
      if (!c.good) return 'Atrasado'
      if (this.isExpiring(c)) return 'Por vencer'
      return 'Al día'
    },
    addRow (obj) { this.list.unshift(obj) },
    updateRow (o) {
      const idx = this.list.findIndex(d => d.id === o.id)
      if (idx !== -1) Object.assign(this.list[idx], o)
    },
    grantedRow (o) {
      const idx = this.list.findIndex(d => d.id === o.clientId)
      if (idx !== -1) Object.assign(this.list[idx], { uid: o.uid, hasAccess: true })
    },
    revokedRow (o) {
      const idx = this.list.findIndex(d => d.id === o.clientId)
      if (idx !== -1) Object.assign(this.list[idx], { hasAccess: false })
    },
    // Cada carga se queda con su número: las consultas vaciaban el array al
    // llamar y empujaban al resolver, así que dos cargas solapadas —cambiar de
    // Activos a Inactivos y volver— terminaban MEZCLADAS en el mismo array, con
    // cada ficha dos veces y todos los contadores al doble.
    async loadInfo () {
      const gen = ++this.loadGen
      try {
        this.$store.dispatch('showProgress')
        await this.getEstates(gen)
        await this.getClients(gen)
      } catch (e) {
        this.$toast.error('Error al cargar inquilinos')
      } finally {
        if (gen === this.loadGen) this.$store.dispatch('hideProgress')
      }
    },
    async getClients (gen) {
      const state = this.filterState === 'true'
      const snap = await db.collection('clients').where('state', '==', state).orderBy('createdAt', 'desc').get()
      const list = []
      snap.forEach(doc => {
        const obj = { id: doc.id, ...doc.data() }
        obj.start = doc.data().start ? doc.data().start.toDate() : null
        obj.end = doc.data().end ? doc.data().end.toDate() : null
        // overdueSince viene como Timestamp de Firestore
        obj.overdueSince = doc.data().overdueSince ? doc.data().overdueSince.toDate() : null
        // good se deriva de overdueSince (fuente única de verdad) — evita
        // divergencias si el campo good quedó desincronizado por imports/seeds
        obj.good = obj.overdueSince === null
        list.push(obj)
      })
      if (gen === this.loadGen) this.list = list
    },
    async getEstates (gen) {
      const snap = await db.collection('estates').get()
      const estates = []
      snap.forEach(doc => estates.push({ id: doc.id, description: doc.data().description }))
      if (gen === this.loadGen) this.estates = estates
    },
    overduePeriod (date) {
      if (!date) return '—'
      const d = date instanceof Date ? date : date.toDate ? date.toDate() : new Date(date)
      return new Intl.DateTimeFormat('es-PE', { month: 'short', year: 'numeric' }).format(d)
    },
    async sendNotification (client) {
      try {
        const responsePdf = await this.generatePDF(client)
        setTimeout(async () => {
          const url = await this.getPdfUrl(responsePdf.id, client.id)
          await db.collection('notifications').add({ url, phone: client.phone, client: { name: client.name } })
        }, 5000)
      } catch (e) {
        this.$toast.error('Error al generar Estado de Cuenta')
      }
    },
    async generatePDF (client) {
      const fees = await this.getBillsPerClient(client)
      const response = await axios.post('https://api.pdfmonkey.io/api/v1/documents', {
        document: {
          document_template_id: '93320869-5E9F-45CE-B464-7680EF45EE4B',
          payload: { sendDate: new Date().getDate() + '/' + (new Date().getMonth() + 1) + '/' + new Date().getFullYear(), client: { name: client.name, phone: client.phone }, fees },
          status: 'pending'
        }
      }, { headers: { Authorization: 'Bearer ' + (process.env.VUE_APP_PDFMONKEY_TOKEN || 'iVtju3Mvy6c2gzhGWerf') } })
      return response.data.document
    },
    async getPdfUrl (id, docId) {
      const response = await axios.get(`https://api.pdfmonkey.io/api/v1/documents/${id}`, { headers: { Authorization: 'Bearer ' + (process.env.VUE_APP_PDFMONKEY_TOKEN || 'iVtju3Mvy6c2gzhGWerf') } })
      const responsePdf = await window.fetch(response.data.document.download_url)
      const storageRef = storage.ref(`home/${docId.substr(0, 6).toUpperCase()}.pdf`)
      const blob = await responsePdf.blob()
      const snapshot = await storageRef.put(blob)
      return snapshot.ref.getDownloadURL()
    },
    async getBillsPerClient (client) {
      const now = new Date()
      const startTime = Timestamp.fromDate(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59))
      const snap = await db.collection('clients').doc(client.id).collection('bills')
        .where('deadline', '<=', startTime).where('paid', '==', false).get()
      const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
      const fees = []
      snap.forEach(doc => {
        const obj = { deadline: doc.data().deadline.toDate(), total: client.currency.code + ' ' + doc.data().total }
        obj.month = months[obj.deadline.getMonth()]
        fees.push(obj)
      })
      return fees
    }
  }
}
</script>
